import { AuthBody } from 'models/AuthBody';
import cookieService, {
  COOKIE_KEYS,
  getTokenCookieExpirationDate,
} from 'services/cookieService';
import localStorageService, {
  LOCAL_STORAGE_KEYS,
} from 'services/localStorageService';

// The DR token is scoped per Data Room so that a user can be authenticated in
// multiple Data Rooms simultaneously (e.g. across browser tabs) without one
// login overwriting another. `dataRoomId` holds the Data Room the current tab
// is viewing and is used to read/write the matching token cookie.
let currentDataRoomId: string | undefined;

const getTokenKey = (dataRoomId: string) =>
  `${COOKIE_KEYS.DR_TOKEN}-${dataRoomId}`;

const getTokenKeys = () =>
  cookieService.getKeys().filter((key) => key.startsWith(getTokenKey('')));

export default {
  set dataRoomId(dataRoomId: string | undefined) {
    currentDataRoomId = dataRoomId;
  },

  get dataRoomId() {
    return currentDataRoomId;
  },

  get token() {
    if (!currentDataRoomId) return undefined;

    return cookieService.getItem<string>(getTokenKey(currentDataRoomId));
  },

  set token(token: string) {
    if (!currentDataRoomId) return;

    cookieService.setItem(getTokenKey(currentDataRoomId), token, {
      expires: getTokenCookieExpirationDate(token),
    });
  },

  removeToken() {
    if (!currentDataRoomId) return;

    cookieService.removeItem(getTokenKey(currentDataRoomId));
  },

  saveAuthBody({ token }: Omit<AuthBody, 'user' | 'refreshToken'>) {
    this.token = token;
  },

  removeAuthBody() {
    this.removeToken();
  },

  removeAllTokens() {
    getTokenKeys().forEach((key) => cookieService.removeItem(key));
  },

  cleanUpTokens() {
    if (localStorageService.getItem(LOCAL_STORAGE_KEYS.DR_TOKENS_CLEANED_UP))
      return;

    getTokenKeys().forEach((key) => {
      const token = cookieService.getItem<string>(key);
      const expirationDate = getTokenCookieExpirationDate(token);

      if (expirationDate)
        cookieService.setItem(key, token, { expires: expirationDate });
      else cookieService.removeItem(key);
    });

    localStorageService.setItem(LOCAL_STORAGE_KEYS.DR_TOKENS_CLEANED_UP, true);
  },
};
